import React, { useContext, useEffect, useState } from "react";
import { Cancel, Key } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  IconButton,
  Typography,
} from "@mui/material";
import CommonApi from "../../../commonApi";
import api from "../../../app/api";
import * as dateFns from "date-fns";
import FileUploadComponent from "../../../app/components/fileUpload";
import { LICENSE_STATUS } from "../../../App";
import appContext from "../../../app/context";
import LicenseInActiveError from "../errors/licInactiveError";
import LicenseExpiredError from "../errors/licExpiredError";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { USER_NAME_ROLE } from "src/app/auth";
import { licenseActions } from "src/contants";

const LicenseImportForm = () => {
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isInactive, setInactive] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const [expiryDate, setExpiryDate] = useState<any>();
  const [selectedFile, setSelectedFile] = useState<any>();
  const [tNcAccepted, setTnCAccepted] = useState<boolean>(false);

  const { licenseStatus, licenseInfo } = useContext(appContext);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setInactive(false);
    setIsExpired(false);
    setHasError(false);

    if (licenseStatus === LICENSE_STATUS.INACTIVE) {
      if (
        licenseInfo !== undefined &&
        licenseInfo.expiryDate &&
        String(licenseInfo.expiryDate)?.trim()?.length > 0
      ) {
        const parsedExpiryDate = dateFns.parse(
          licenseInfo.expiryDate,
          "yyyy-MM-dd HH:mm:ss",
          new Date()
        );

        const currentDate = new Date();
        if (dateFns.isBefore(parsedExpiryDate, currentDate)) {
          setIsExpired(true);
          setExpiryDate(parsedExpiryDate);
          return;
        } else {
          setInactive(true);
        }
      }
      setInactive(true);
    } else if (licenseStatus === LICENSE_STATUS.ACTIVE) {
      setInactive(false);
      setIsExpired(false);
    }
  }, [licenseStatus, licenseInfo]);


  const onClickActivateButton = async () => {
    try {
      // Step 1: Import license
      const res = await CommonApi.importLicense({ selectedFile });

      enqueueSnackbar({
        message:
          (res?.data?.Status === "Success" && "Data imported.") +
          " , Please refresh the page",
        variant: "success",
      });

      // Step 2: Add license log (fire and wait)
      try {
        await CommonApi.addLicenseLog({
          license_number: res?.data?.data?.licenseNumber || "",
          activation_date: res?.data?.data?.approvedDate || "",
          expiry_date: res?.data?.data?.license_expiry_date || "",
          serial_no: res?.data?.data?.vbox_serial_number || "",
          created_by:
            sessionStorage.getItem(USER_NAME_ROLE) === "admin" ? 1 : 2,
          action: licenseActions["IMPORTED"],
        });

        console.log("License log added successfully.");
      } catch (logErr: any) {
        console.error("Failed to add license log:", logErr);
      }

      // Step 3: Backup to Initial
      try {
        const backupRes = await CommonApi.backupToInitial();

        enqueueSnackbar({
          message:
            backupRes?.data?.Message ||
            "Database backup to Initial successful.",
          variant: "info",
        });
      } catch (backupErr: any) {
        enqueueSnackbar({
          message:
            backupErr?.response?.data?.Message ||
            "License imported, but backup to Initial failed.",
          variant: "warning",
        });
      }

      // Step 4: Redirect (same behavior as your original code)
      window.open("/", "_self");
    } catch (e: any) {
      // Import failed
      setHasError(true);

      enqueueSnackbar({
        message:
          e?.response?.data?.Message ||
          e?.message ||
          e?.Message ||
          "Something went wrong, please contact the administrator!",
        variant: "error",
      });
    }
  };


  const onChangeLicenseFile = (licFile: any) => {
    setSelectedFile(licFile);
  };

  const onChangeTnc = (e: any, checked: boolean) => {
    setTnCAccepted(checked);
  };

  const handleClick = () => {
    const date = new Date();
    const toSendDate =
      date.getUTCFullYear() +
      "-" +
      (date.getUTCMonth() + 1).toString().padStart(2, "0") +
      "-" +
      date.getUTCDate().toString().padStart(2, "0") +
      " " +
      date.getUTCHours().toString().padStart(2, "0") +
      ":" +
      date.getUTCMinutes().toString().padStart(2, "0") +
      ":" +
      date.getUTCSeconds().toString().padStart(2, "0");

    console.log("date", toSendDate);
    api
      .get(`/vbox/synk_time.php?synk_time=${toSendDate}`)
      .then((res) => {
        enqueueSnackbar({
          message: res?.data?.Message,
          variant: "success",
        });
        window.location.reload();
      })
      .catch((e) => {
        enqueueSnackbar({
          message: e?.response?.data?.Message || "",
          variant: "error",
        });
      });
  };
  const userNameRole = sessionStorage.getItem(USER_NAME_ROLE);

  return (
    <Box sx={{ textAlign: "left", maxWidth: "416px" }}>
      <Box
        sx={{
          maxWidth: "416px",
          mt: { md: 4, sm: 4, xs: 4 },
          textAlign: "center",
        }}
      >
        <img alt="logo" src="logo_vib_360.png" />
      </Box>

      {hasError && userNameRole === "admin" && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            size="small"
            sx={{ mr: "18px" }}
            variant="outlined"
            onClick={handleClick}
          >
            Synchronize Time Difference
          </Button>
        </Box>
      )}

      {isExpired && (
        <Box sx={{ maxWidth: isExpired ? "auto" : "416px", mt: 1 }}>
          <LicenseExpiredError />
          <>
            <Box sx={{ mt: 1 }}>
              <Typography>
                {" "}
                {t("login.lic.expired.msg.part1", { ns: "root" })}{" "}
                <b> {`${dateFns.format(expiryDate, "dd MMM yyyy, H:mm a")}`}</b>
                , {t("login.lic.expired.msg.part2", { ns: "root" })}{" "}
                <a
                  href={`${process.env.REACT_APP_LIC_PORTAL_URL}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("login.lic.expired.msg.part3", { ns: "root" })}
                </a>{" "}
                {t("login.lic.expired.msg.part4", { ns: "root" })}
              </Typography>
            </Box>
          </>
        </Box>
      )}
      {isInactive && (
        <Box sx={{ maxWidth: isInactive ? "auto" : "416px", mt: 1 }}>
          <LicenseInActiveError />
        </Box>
      )}
      <Box sx={{ mt: 1 }}>
        <Box>
          <FormLabel>{t("login.lic.upload.file", { ns: "root" })}</FormLabel>
          {selectedFile ? (
            <Box
              sx={{
                border: "1px solid #1D4580",
                borderRadius: "8px",
                height: "100px",
                display: "flex",
                mt: 1,
                alignItems: "center",
                position: "relative",
              }}
            >
              <Box display="flex" sx={{ flex: 1 }}>
                <Box sx={{ mr: 2, p: 1 }}>
                  <Key sx={{ fontSize: "36px" }} />
                </Box>
                <Box sx={{ pr: 2 }}>
                  <Typography variant="body1" sx={{ fontSize: "16px" }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption">
                    {selectedFile.size} bytes
                  </Typography>
                </Box>

                <Box sx={{ position: "absolute", top: "10px", right: "10px" }}>
                  <IconButton onClick={() => setSelectedFile(undefined)}>
                    <Cancel />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ) : (
            <FileUploadComponent
              onChangeHandler={onChangeLicenseFile}
              accept=".dat"
            ></FileUploadComponent>
          )}
        </Box>
        <Box>
          <Checkbox
            id="termsAndConditionsAccept"
            value={tNcAccepted}
            onChange={onChangeTnc}
          ></Checkbox>{" "}
          {t("login.lic.agree.part1", { ns: "root" })}{" "}
          <a
            href={
              process.env.PUBLIC_URL +
              `/Annex VIII_VIB 360 End user license contract_Distrib agreement [FR].pdf`
            }
            target="_blank"
            rel="noreferrer"
          >
            {t("login.lic.agree.part2", { ns: "root" })}
          </a>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            id="activate-btn"
            color="primary"
            variant="contained"
            disabled={!(tNcAccepted && selectedFile !== undefined)}
            onClick={onClickActivateButton}
          >
            {t("login.lic.btn.activate", { ns: "root" })}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
export default LicenseImportForm;
